import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Lock, LockOpen, Warning, ArrowRight, Sparkle, UserFocus } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { AppShell } from "@/components/AppShell";
import { Spinner } from "@/components/Spinner";
import { ScoreGauge } from "@/components/ScoreGauge";
import { DisclaimerNote } from "@/components/DisclaimerNote";
import { EvidenceConfidence } from "@/components/EvidenceConfidence";
import { CompleteScopeDialog, ScopeValidationWarnings } from "@/components/ScopeTools";
import { api, apiError, trackEvent, SEVERITY_STYLES, FRAMEWORK_LABELS, FRAMEWORK_KEYS } from "@/lib/api";

export default function Results() {
  const { assessmentId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [meta, setMeta] = useState(null);
  const [buying, setBuying] = useState(false);
  const [reviewNotes, setReviewNotes] = useState("");
  const [reviewSent, setReviewSent] = useState(false);

  const load = () =>
    api
      .get(`/assessments/${assessmentId}/results`)
      .then(({ data: d }) => setData(d))
      .catch((e) => toast.error(apiError(e)));

  useEffect(() => {
    api.get("/meta").then(({ data: m }) => setMeta(m)).catch(() => {});
    api
      .get(`/assessments/${assessmentId}/results`)
      .then(({ data: d }) => {
        setData(d);
        trackEvent("paywall_viewed", { assessment_id: assessmentId });
      })
      .catch((e) => {
        toast.error(apiError(e));
        navigate("/dashboard");
      });
  }, [assessmentId, navigate]);

  const unlock = async () => {
    setBuying(true);
    try {
      await api.post(`/assessments/${assessmentId}/purchase`);
      toast.success("Report unlocked");
      navigate(`/report/${assessmentId}`);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBuying(false);
    }
  };

  const requestReview = async () => {
    try {
      await api.post(`/assessments/${assessmentId}/expert-review`, { notes: reviewNotes });
      setReviewSent(true);
      toast.success("Expert review requested");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  if (!data)
    return (
      <AppShell breadcrumb="results">
        <Spinner label="Computing readiness" />
      </AppShell>
    );

  const c = data.severity_counts;

  return (
    <AppShell breadcrumb={`results / ${data.product.name}`}>
      <div className="max-w-5xl">
        <p className="overline text-navy-800">Indicative readiness · free view</p>
        <h1 className="mt-2 font-display text-3xl font-extrabold text-navy-900 lg:text-4xl">
          {data.product.name}
        </h1>
        <p className="mt-3 max-w-2xl text-sm leading-relaxed text-slate-600">
          Deterministic scores based on your answers. Yes counts 2, Partially 1, No and Don't Know 0.
          Not Applicable answers are excluded from scoring entirely.
        </p>
      </div>

      <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3 stagger" data-testid="framework-scores">
        {FRAMEWORK_KEYS.map((k) => (
          <ScoreGauge key={k} frameworkKey={k} data={data.framework_scores[k]} compact />
        ))}
      </div>

      <div className="mt-4 grid gap-4 lg:grid-cols-[1fr_1.4fr]">
        <EvidenceConfidence
          level={data.evidence_confidence}
          coverage={data.evidence_coverage_pct}
          unknownCount={data.uncertainty_count}
        />
        <div className="border border-slate-200 bg-white p-5" data-testid="severity-context">
          <p className="overline text-navy-800">How severity is weighted for this product</p>
          <p className="mt-3 text-sm leading-relaxed text-slate-600">
            Severity is{" "}
            <span className="font-mono text-xs">importance × answer quality × product context</span>, so the
            same gap ranks differently depending on the product. Yours carries an exposure factor of{" "}
            <strong>{data.severity_context?.exposure_factor}</strong> ({data.severity_context?.exposure_reason}) and a
            consequence factor of <strong>{data.severity_context?.consequence_factor}</strong>{" "}
            ({data.severity_context?.consequence_reason}), giving a context multiplier of{" "}
            <strong>{data.severity_context?.context_factor}</strong>.
          </p>
        </div>
      </div>

      {data.updated_since_purchase && (
        <div className="mt-6 border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800" data-testid="updated-since-purchase-banner">
          <strong>Updated since purchase.</strong> Your answers changed after the last report purchase.
          The purchased report stays an immutable snapshot; this free preview reflects the new answers,
          and a new purchase is required for an updated report.
        </div>
      )}

      <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {[
          ["Critical", c.critical, "text-rose-700"],
          ["High", c.high, "text-amber-700"],
          ["Medium", c.medium, "text-teal-700"],
          ["Low", c.low, "text-slate-600"],
        ].map(([label, value, cls]) => (
          <div key={label} className="border border-slate-200 bg-white p-5" data-testid={`severity-count-${label.toLowerCase()}`}>
            <p className="overline text-slate-500">{label} gaps</p>
            <p className={`mt-2 font-display text-3xl font-extrabold ${cls}`}>{value}</p>
          </div>
        ))}
      </div>

      {data.scope_validation?.length > 0 && (
        <div className="mt-6">
          <ScopeValidationWarnings warnings={data.scope_validation} />
        </div>
      )}

      {data.uncertainty_count > 0 && (
        <div className="mt-6 flex gap-3 border border-slate-300 bg-white p-5" data-testid="uncertainty-callout">
          <Warning size={22} className="mt-0.5 shrink-0 text-amber-700" />
          <div>
            <h2 className="font-display text-base font-bold text-navy-900">
              {data.uncertainty_count} answers marked Don't Know, evidence confidence {data.evidence_confidence}
            </h2>
            <p className="mt-2 max-w-3xl text-sm leading-relaxed text-slate-600">
              These score neutrally, so they do not artificially lower your readiness number. Instead they lower
              evidence confidence, which is reported separately: readiness measures your answers, confidence
              measures how well those answers are known. Any customer or auditor conversation will land on
              exactly these points, so validate them internally before you rely on the scores above.
            </p>
          </div>
        </div>
      )}

      <section className="mt-12 grid gap-8 lg:grid-cols-[1.3fr_1fr]">
        <div>
          <p className="overline text-navy-800">Cross-framework impact</p>
          <h2 className="mt-2 font-display text-2xl font-bold text-navy-900">Your top potential gaps</h2>
          <p className="mt-2 text-sm text-slate-600">
            Showing {data.preview_findings.length} of {data.total_findings} findings.
          </p>

          <div className="mt-6 space-y-3 stagger" data-testid="preview-findings">
            {data.preview_findings.map((f) => (
              <div key={f.question_code} className="border border-slate-200 bg-white p-5" data-testid={`preview-finding-${f.question_code}`}>
                <div className="flex flex-wrap items-center gap-2">
                  <span className={`border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${SEVERITY_STYLES[f.severity]}`}>
                    {f.severity}
                  </span>
                  <span className="font-mono text-[11px] text-slate-500">{f.question_code}</span>
                  <span className="text-[11px] text-slate-500">{f.section_name}</span>
                  {f.uncertainty && (
                    <span className="border border-amber-200 bg-amber-50 px-2 py-0.5 text-[10px] uppercase tracking-wide text-amber-700">
                      Don't know
                    </span>
                  )}
                </div>
                <h3 className="mt-3 font-display text-base font-bold text-navy-900">{f.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{f.cross_framework_impact}</p>
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {f.frameworks.map((x) => (
                    <span key={x} className="border border-navy-800/20 bg-blue-50 px-2 py-0.5 font-mono text-[10px] text-navy-800">
                      {FRAMEWORK_LABELS[x]}
                    </span>
                  ))}
                </div>
                <p className="mt-3 text-xs text-slate-500">Your response: {f.answer_label}</p>
              </div>
            ))}
          </div>

          {data.locked_findings > 0 && (
            <div className="relative mt-4 overflow-hidden border border-slate-200 bg-white" data-testid="locked-findings-panel">
              <div className="pointer-events-none select-none p-5 blur-[5px]" aria-hidden="true">
                {[0, 1, 2].map((i) => (
                  <div key={i} className="mb-4">
                    <div className="h-3 w-24 bg-slate-200" />
                    <div className="mt-2 h-4 w-2/3 bg-slate-300" />
                    <div className="mt-2 h-3 w-full bg-slate-200" />
                    <div className="mt-1.5 h-3 w-5/6 bg-slate-200" />
                  </div>
                ))}
              </div>
              <div className="absolute inset-0 flex flex-col items-center justify-center bg-white/70 p-6 text-center">
                <Lock size={26} className="text-navy-800" />
                <p className="mt-3 font-display text-lg font-bold text-navy-900" data-testid="locked-findings-count">
                  {data.locked_findings} further findings in the full report
                </p>
                <p className="mt-1.5 max-w-sm text-xs leading-relaxed text-slate-600">
                  Evidence guidance, remediation actions and the 30/60/90 day roadmap are part of the
                  detailed report.
                </p>
              </div>
            </div>
          )}
        </div>

        <aside className="space-y-4 lg:sticky lg:top-20 lg:self-start">
          <div className="border-2 border-navy-800 bg-white p-6" data-testid="paywall-card">
            <p className="overline text-navy-800">Full readiness report</p>
            <p className="mt-3 font-display text-3xl font-extrabold text-navy-900">
              Free while in early access
            </p>
            <p className="mt-1 text-xs text-slate-500">Unlimited reassessment, no card required</p>
            <ul className="mt-5 space-y-2 text-sm text-slate-700">
              {[
                "Assessment scope page: product, versions, deployment, interfaces, markets",
                "Executive summary for management",
                "Top 5 prioritised actions with effort and evidence needed",
                `Full findings register (${data.total_findings} findings)`,
                "How each score is calculated, point by point",
                "Sequenced 30/60/90 day roadmap with dependencies",
                "PDF export",
              ].map((x) => (
                <li key={x} className="flex gap-2">
                  <Sparkle size={14} className="mt-1 shrink-0 text-navy-800" /> {x}
                </li>
              ))}
            </ul>
            {data.missing_scope_fields?.length > 0 && (
              <div className="mt-6">
                <CompleteScopeDialog
                  product={data.product}
                  meta={meta}
                  missing={data.missing_scope_fields}
                  onSaved={load}
                />
              </div>
            )}
            {data.purchased && !data.updated_since_purchase ? (
              <Button
                className="mt-6 w-full bg-navy-800 text-white hover:bg-navy-700"
                onClick={() => navigate(`/report/${assessmentId}`)}
                data-testid="view-purchased-report-button"
              >
                <LockOpen size={15} className="mr-2" /> Open your report
              </Button>
            ) : (
              <Button
                className="mt-6 w-full bg-navy-800 text-white hover:bg-navy-700"
                disabled={buying || data.missing_scope_fields?.length > 0}
                onClick={unlock}
                data-testid="unlock-report-button"
              >
                {buying ? "Generating…" : "Get the full report"} <ArrowRight size={15} className="ml-2" />
              </Button>
            )}
            <p className="mt-3 text-[11px] leading-relaxed text-slate-500" data-testid="entitlement-note">
              Generate and regenerate the report for this assessment as often as you need. Outputs stay
              indicative and are not a compliance statement.
            </p>
          </div>

          <div className="border border-slate-200 bg-white p-6" data-testid="expert-review-card">
            <UserFocus size={22} weight="duotone" className="text-navy-800" />
            <p className="mt-3 font-display text-base font-bold text-navy-900">
              Request an expert review
            </p>
            <p className="mt-2 text-xs leading-relaxed text-slate-600">
              A product security reviewer walks your findings register, validates Don't Know areas and
              prioritises against your release plan. Request first, scope confirmed before any work.
            </p>
            <p className="mt-2 text-xs leading-relaxed text-slate-600" data-testid="expert-review-email-note">
              Expert review is arranged by email. Send your request here or write to{" "}
              <a
                href={`mailto:${data.support_email}`}
                className="font-semibold text-navy-800 underline"
                data-testid="expert-review-email-link"
              >
                {data.support_email}
              </a>
              , and a reviewer confirms scope and invoicing before any work starts.
            </p>
            {reviewSent ? (
              <p className="mt-4 border border-slate-200 bg-slate-100/70 p-3 text-xs text-slate-700" data-testid="expert-review-sent">
                Request received. A reviewer will follow up by email; track status from this page.
              </p>
            ) : (
              <Dialog>
                <DialogTrigger asChild>
                  <Button variant="outline" className="mt-4 w-full" data-testid="request-expert-review-button">
                    Request expert review
                  </Button>
                </DialogTrigger>
                <DialogContent data-testid="expert-review-dialog">
                  <DialogHeader>
                    <DialogTitle>Request an expert review</DialogTitle>
                    <DialogDescription>
                      Tell the reviewer what matters most, release dates, customer questions, specific
                      interfaces.
                    </DialogDescription>
                  </DialogHeader>
                  <Textarea
                    rows={4}
                    placeholder="Context for the reviewer (optional)"
                    value={reviewNotes}
                    onChange={(e) => setReviewNotes(e.target.value)}
                    data-testid="expert-review-notes-input"
                  />
                  <DialogFooter>
                    <Button
                      className="bg-navy-800 text-white hover:bg-navy-700"
                      onClick={requestReview}
                      data-testid="submit-expert-review-button"
                    >
                      Send request
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            )}
          </div>

          <div className="border border-slate-200 bg-white p-6">
            <p className="overline text-navy-800">Strengths observed</p>
            <p className="mt-2 text-xs text-slate-500">{data.strength_count} areas fully affirmed</p>
            <ul className="mt-4 space-y-2 text-xs leading-relaxed text-slate-700" data-testid="strengths-list">
              {data.strengths.map((s) => (
                <li key={s.question_code}>• {s.title}</li>
              ))}
              {data.strengths.length === 0 && <li className="text-slate-500">No fully affirmed areas yet.</li>}
            </ul>
          </div>
        </aside>
      </section>

      <div className="mt-12 max-w-4xl">
        <DisclaimerNote text={data.disclaimer} testId="results-disclaimer" />
      </div>
    </AppShell>
  );
}
